import { useLocation } from 'react-router-dom';
import { getSeo } from './seo-data';
import { SITE_NAME, SITE_URL, SITE_IMAGE, LAST_MODIFIED } from './seo-data';

const FAQ_ITEMS = [
  {
    '@type': 'Question',
    name: 'What is the CSSBuy Spreadsheet?',
    acceptedAnswer: {
      '@type': 'Answer',
      text: 'The CSSBuy Spreadsheet is an organized way to discover and compare products, links and resources related to CSSBuy, including QC guides, shipping information and buying tips.',
    },
  },
  {
    '@type': 'Question',
    name: 'Is CSSBuy safe to use?',
    acceptedAnswer: {
      '@type': 'Answer',
      text: 'CSSBuy is a known buying agent platform, but users should always verify sellers, review QC photos and follow standard online shopping safety practices.',
    },
  },
  {
    '@type': 'Question',
    name: 'How much does CSSBuy shipping cost?',
    acceptedAnswer: {
      '@type': 'Answer',
      text: 'CSSBuy shipping costs depend on weight, volume, destination and the chosen shipping line, so you should verify current rates before placing an order.',
    },
  },
  {
    '@type': 'Question',
    name: 'What is CSSBuy quality control (QC)?',
    acceptedAnswer: {
      '@type': 'Answer',
      text: 'CSSBuy quality control provides photos of your purchased items at the warehouse so you can inspect them for defects or wrong items before shipping.',
    },
  },
];

const organization = {
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: SITE_IMAGE,
};

export function buildJsonLd(pathname: string): object[] {
  const seo = getSeo(pathname);
  const url = seo.path === '/' ? `${SITE_URL}/` : `${SITE_URL}${seo.path}`;

  if (pathname === '/') {
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: SITE_NAME,
        url,
        logo: SITE_IMAGE,
        sameAs: ['https://kaspreadsheet.com'],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: SITE_NAME,
        url,
        description: seo.description,
        inLanguage: 'en',
        publisher: organization,
      },
    ];
  }

  if (seo.type === 'article') {
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: seo.title,
        description: seo.description,
        url,
        mainEntityOfPage: url,
        inLanguage: 'en',
        author: organization,
        publisher: organization,
        datePublished: LAST_MODIFIED,
        dateModified: LAST_MODIFIED,
        image: SITE_IMAGE,
      },
    ];
  }

  if (seo.type === 'faq') {
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQ_ITEMS,
      },
    ];
  }

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: seo.title,
      description: seo.description,
      url,
      inLanguage: 'en',
      isPartOf: { '@type': 'WebSite', name: SITE_NAME, url: SITE_URL },
    },
  ];
}

export default function JsonLd() {
  const { pathname } = useLocation();
  const items = buildJsonLd(pathname);
  return (
    <>
      {items.map((item, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(item) }}
        />
      ))}
    </>
  );
}
